import React from 'react';
import { Link } from 'react-router-dom';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-6">
          <div className="bg-white border border-red-200 rounded-lg p-8 max-w-md text-center">
            <h2 className="font-condensed font-bold text-xl uppercase text-red-600 mb-4">
              Something went wrong
            </h2>
            <p className="font-sans text-sm text-gray-600 mb-6">
              An error occurred while loading this page. Please try again.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => this.setState({ hasError: false, error: null })}
                className="bg-primary border border-foreground rounded h-12 px-6 flex items-center justify-center hover:opacity-90 transition-opacity"
              >
                <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-white">
                  Try Again
                </span>
              </button>
              <Link
                to="/"
                onClick={() => this.setState({ hasError: false, error: null })}
                className="bg-white border border-foreground rounded h-12 px-6 flex items-center justify-center hover:opacity-90 transition-opacity"
              >
                <span className="font-mono font-medium text-sm tracking-[0.7px] uppercase text-foreground">
                  Go Home
                </span>
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
